import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './KeyAccountPO.css';

/**
 * B&N Shipping Labels — every open Barnes & Noble PO, read from its own PO fax,
 * with a Print button. The server re-reads the stored PO document on every print,
 * so the PO number, ship-to and SAN on the label can only be what B&N's PO says.
 * Layout approved by B&N Label Approval 2026-10-19. See keyAccountShipLabels.js.
 */

function sanDisplay(san) {
  const s = String(san || '');
  return s.length === 7 ? `${s.slice(0, 3)}-${s.slice(3, 6)}-${s.slice(6)}` : s;
}

function UploadFax({ poNumber, onDone }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    try {
      setBusy('Reading the PO');
      const form = new FormData();
      form.append('file', file);
      const p = await axios.post('/api/key-account-po/parse', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const pos = (p.data.pos || []).map((x) => ({ poNumber: x.poNumber, poDate: x.poDate }));
      if (!pos.some((x) => String(x.poNumber) === String(poNumber))) {
        throw new Error(`That file is not PO ${poNumber} (it has ${pos.map((x) => x.poNumber).join(', ') || 'no PO number'})`);
      }
      setBusy('Storing it on the sales order');
      const a = new FormData();
      a.append('file', file);
      a.append('pos', JSON.stringify(pos));
      await axios.post('/api/key-account-po/attach', a, { headers: { 'Content-Type': 'multipart/form-data' } });
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <div>
      <input type="file" accept=".pdf,application/pdf" onChange={onFile} disabled={!!busy} />
      {busy && <div className="ka-hint">{busy}&hellip;</div>}
      {error && <div className="ka-error">{error}</div>}
    </div>
  );
}

export default function BnShippingLabels() {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState({});
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get('/api/key-account-po/ship-labels/open');
      setRows(res.data.rows || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const print = async (po) => {
    const count = Number(counts[po]);
    if (!Number.isInteger(count) || count < 1) return;
    // Open the tab before the request so the browser does not block it as a popup.
    const tab = window.open('', '_blank');
    setBusy((b) => ({ ...b, [po]: true }));
    setError('');
    try {
      const res = await axios.post('/api/key-account-po/ship-labels/print', { poNumber: po, count }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (tab) tab.location.href = url; else window.location.href = url;
    } catch (err) {
      if (tab) tab.close();
      let msg = err.message;
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch { /* keep message */ }
      }
      setError(`PO ${po}: ${msg}`);
    } finally {
      setBusy((b) => ({ ...b, [po]: false }));
    }
  };

  return (
    <div className="ka-app">
      <header className="ka-header">
        <div>
          <h1>B&amp;N Shipping Labels</h1>
          <p className="ka-sub">Open Barnes &amp; Noble POs &middot; print 4x6 shipping labels on the Rollo</p>
        </div>
        <div className="ka-note">
          The PO number, ship-to and SAN on every label are read from B&amp;N&apos;s own PO fax. Nothing is typed in.
        </div>
      </header>

      <section className="ka-card">
        <div className="ka-field-label">How many labels</div>
        <div className="ka-hint">
          A pallet with one item from one PO needs one label, on the top carton under the shrink wrap.
          A mixed pallet needs a label on every carton. Every carton still needs its carton product label.
        </div>
        <div className="ka-hint">
          Printing: the label opens as a PDF. Print to the Rollo, paper 4x6, scale 100% (actual size).
        </div>
        {error && <div className="ka-error">{error}</div>}
      </section>

      <section className="ka-card">
        <div className="ka-field-label">Open POs</div>
        {rows === null && <div className="ka-hint">Reading the PO documents&hellip;</div>}
        {rows && rows.length === 0 && !error && <div className="ka-hint">No open B&amp;N sales orders.</div>}
        {rows && rows.length > 0 && (
          <table className="ka-table">
            <thead>
              <tr>
                <th>PO</th><th>Ship to (from the PO)</th><th>SAN</th><th>Sales order</th><th>Ship by</th>
                <th>Cancel</th><th>Cartons on PO</th><th>Labels</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.poNumber} className={r.error ? 'ka-row-warn' : ''}>
                  <td><strong>{r.poNumber}</strong>{r.sourceFile && <><br /><small>{r.sourceFile}</small></>}</td>
                  {r.error ? (
                    <td colSpan={2}>
                      <div className="ka-bad">{r.error}</div>
                      <UploadFax poNumber={r.poNumber} onDone={load} />
                    </td>
                  ) : (
                    <>
                      <td>{r.label.shipTo.map((l, i) => <div key={i}>{l}</div>)}</td>
                      <td>{r.label.city}<br />{sanDisplay(r.label.san)}</td>
                    </>
                  )}
                  <td>{r.tranid}<br /><small>{r.status}</small></td>
                  <td>{r.shipDate}</td>
                  <td>{r.cancelDate || ''}</td>
                  <td>{r.cartons || ''}</td>
                  <td>
                    {!r.error && (
                      <input
                        className="ka-input"
                        type="number"
                        min="1"
                        step="1"
                        style={{ width: 70 }}
                        value={counts[r.poNumber] || ''}
                        onChange={(e) => setCounts((c) => ({ ...c, [r.poNumber]: e.target.value }))}
                      />
                    )}
                  </td>
                  <td>
                    {!r.error && (
                      <button
                        type="button"
                        className="ka-btn ka-btn-primary"
                        disabled={!(Number(counts[r.poNumber]) >= 1) || busy[r.poNumber]}
                        onClick={() => print(r.poNumber)}
                      >
                        {busy[r.poNumber] ? 'Building' : 'Print labels'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
